import React, { useContext, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { AuthContext } from '../../../context/auth';
import { GET } from '../../../utils/requests';
import { getuseruri } from '../../../uri';
import { TYPES, PRODUCT_TYPES } from '../../../utils/type';
import { Badge, Card, Collapse, Spin, Tag, Button, Modal, Input, Form } from 'antd';
import { CaretRightOutlined, DollarOutlined, ClockCircleOutlined } from '@ant-design/icons';

const { Panel } = Collapse;

const OrderCard = ({ order, refreshData }) => {
  const toast = useToast();
  const { currentUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [isCargoModalVisible, setIsCargoModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [cargoPrice, setCargoPrice] = useState('');
  const [form] = Form.useForm();

  // Get payment status display text
  const getPaymentStatusDisplay = (status) => {
    switch (status) {
      case TYPES.REQUESTED:
        return { text: 'Төлөгдөөгүй', color: 'orange' };
      case TYPES.APPROVED:
        return { text: 'Төлөгдсөн', color: 'green' };
      default:
        return { text: 'Төлбөрийн мэдээлэл олдсонгүй', color: 'red' };
    }
  };

  // Get order item status text and color
  const getOrderItemStatus = (status) => {
    switch (status) {
      case TYPES.REQUESTED:
        return { text: 'Хүлээгдэж байгаа', color: 'processing' };
      case TYPES.IRSEN:
        return { text: 'Ирсэн', color: 'success' };
      case TYPES.CONFIRM:
        return { text: 'Авсан', color: 'cyan' };
      default:
        return { text: 'Төлөв тодорхойгүй', color: 'default' };
    }
  };

  // Handle marking a special order item as arrived
  const handleItemArrived = (item) => {
    setSelectedItem(item);
    setIsCargoModalVisible(true);
  };

  // Handle cargo price submission
  const handleCargoSubmit = async () => {
    if (!cargoPrice || isNaN(parseFloat(cargoPrice))) {
      toast({
        title: 'Алдаа',
        description: 'Зөв дүн оруулна уу',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      // This endpoint needs to be implemented on the backend
      const res = await GET(
        getuseruri + `/order/item/update?token=${currentUser}&id=${selectedItem.id}&status=${TYPES.IRSEN}&cargo=${parseFloat(cargoPrice)}`
      );
      
      if (res.status === 200) {
        toast({
          title: 'Амжилттай',
          description: 'Барааны мэдээлэл шинэчлэгдлээ',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        setCargoPrice('');
        setIsCargoModalVisible(false);
        refreshData();
      }
    } catch (error) {
      toast({
        title: 'Алдаа гарлаа',
        description: error.message || 'Дахин оролдоно уу',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Separate items by product type
  const readyItems = order.order_items.filter(item => 
    item.product.product_type === PRODUCT_TYPES.BELEN
  );
  
  const specialOrderItems = order.order_items.filter(item => 
    item.product.product_type !== PRODUCT_TYPES.BELEN
  );

  const paymentStatus = getPaymentStatusDisplay(order.status);

  return (
    <Card 
      className="order-card" 
      title={
        <div className="flex justify-between items-center">
          <span>Захиалга #{order.id}</span>
          <Tag color={paymentStatus.color}>{paymentStatus.text}</Tag>
        </div>
      }
      extra={
        <span>Нийт дүн: {order.total_amount?.toLocaleString()} ₮</span>
      }
    >
      {/* Ready products section */}
      {readyItems.length > 0 && (
        <div className="mb-4">
          <h3 className="font-bold mb-2">Бэлэн бараанууд</h3>
          {readyItems.map((item, index) => {
            const status = getOrderItemStatus(item.status);
            return (
              <div key={`ready-${index}`} className="bg-gray-50 p-3 rounded-md mb-2">
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-bold">{item.product.name}</h4>
                    <div className="text-sm grid grid-cols-2 gap-2 mt-1">
                      <span>Ширхэг: {item.quantity}</span>
                      <span>Хэмжээ: {item.size?.value || "N/A"}</span>
                      <span>Өнгө: {item.color?.name || "N/A"}</span>
                      <span>Үнэ: {item.price_at_purchase?.toLocaleString() || 0} ₮</span>
                    </div>
                  </div>
                  <Badge status={status.color} text={status.text} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Special order products section */}
      {specialOrderItems.length > 0 && (
        <div>
          <h3 className="font-bold mb-2">Захиалгын бараанууд</h3>
          {specialOrderItems.map((item, index) => {
            const status = getOrderItemStatus(item.status);
            return (
              <div key={`special-${index}`} className="bg-gray-50 p-3 rounded-md mb-2 border-l-4 border-blue-500">
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-bold">{item.product.name}</h4>
                    <div className="text-sm grid grid-cols-2 gap-2 mt-1">
                      <span>Ширхэг: {item.quantity}</span>
                      <span>Хэмжээ: {item.size?.value || "N/A"}</span>
                      <span>Өнгө: {item.color?.name || "N/A"}</span>
                      <span>Үнэ: {item.price_at_purchase?.toLocaleString() || 0} ₮</span>
                      {item.cargo && <span>Карго: {item.cargo?.toLocaleString() || 0} ₮</span>}
                    </div>
                  </div>
                  <div>
                    <Badge status={status.color} text={status.text} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

     
    </Card>
  );
};

export default OrderCard;